// P5.2 — TEAM Core User Settings + Profile Data — sidebar page.
//
// Pepe spec (Feb 2026):
//   Q1.1 — Dual entry: tab in dashboard + avatar link.
//   Q1.2 — Sidebar left on desktop, accordion on mobile.
//   Q1.3 — Completion bar always visible inside Settings; dashboard
//          shows it only when < 80%.
//
// Sections rendered:
//   account, calendar, notifications, social_links, relationships,
//   favorites, hobbies, network (stub), messaging (stub),
//   wishlist_preferences, travel, profile_data (Level 1 + Level 2 only
//   in P5.2; Level 3 dynamic blocks land in P5.3).
//
// Each section gets a thin section-component that:
//   • Reads the current section value from /api/users/me/settings.
//   • PUT-saves on a "Save" button (explicit save preferred to debounced
//     auto-save here — see Pepe Q1.4 deferred; current default keeps UX
//     predictable for the first round of QA).
//
// Sections still on placeholder copy (Family/Guardian + Sport Relationships
// + Friends/Network + Messaging) render "Coming soon" cards per Pepe rule.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import PanelLayout from "@/components/layout/PanelLayout";
import ProfileCompletionBar from "@/components/settings/ProfileCompletionBar";
import ActiveProfileSelector from "@/components/settings/ActiveProfileSelector";
import MedicalConsentBanner from "@/components/settings/MedicalConsentBanner";
import { SECTION_FIELDS } from "@/pages/user/sectionsConfig";
import { PROFILE_BLOCK_FIELDS, MEDICAL_SENSITIVE_PROFILES } from "@/pages/user/profileBlockConfigs";
import BrandMultiSelect from "@/components/wishlist/BrandMultiSelect";
import ProductCategoryMultiSelect from "@/components/wishlist/ProductCategoryMultiSelect";
import SponsorshipCategoryMultiSelect from "@/components/wishlist/SponsorshipCategoryMultiSelect";
import WishlistAdoptionBanner from "@/components/wlp/WishlistAdoptionBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, User as UserIcon, CalendarDays, Bell, AtSign, Users, Star,
  Sparkles, UserPlus, MessageSquare, Heart, Plane, ClipboardList, ChevronDown,
  Clock, ShieldCheck, Lock, Wrench, Store, Languages as LanguagesIcon,
} from "lucide-react";

const SECTION_ORDER = [
  { key: "account",              icon: UserIcon,       comingSoon: false },
  { key: "calendar",             icon: CalendarDays,   comingSoon: false },
  { key: "notifications",        icon: Bell,           comingSoon: false },
  { key: "social_links",         icon: AtSign,         comingSoon: false },
  { key: "relationships",        icon: Users,          comingSoon: true  },
  { key: "favorites",            icon: Star,           comingSoon: false },
  { key: "hobbies",              icon: Sparkles,       comingSoon: false },
  { key: "network",              icon: UserPlus,       comingSoon: true  },
  { key: "messaging",            icon: MessageSquare,  comingSoon: true  },
  { key: "wishlist_preferences", icon: Heart,          comingSoon: false },
  { key: "travel",               icon: Plane,          comingSoon: false },
  { key: "profile_data",         icon: ClipboardList,  comingSoon: false },
  // PROFILE-SETTINGS-ALL-PROFILES-ALIGN-1 (Feb 2026): per-role
  // placeholder sections. All `comingSoon: true` — pending GO from
  // José/Lumia to define real backend-backed preferences per role.
  // Surfaced via DashboardSideMenu matrix `SETTINGS_VISIBILITY_BY_KEY`.
  { key: "professional_prefs",   icon: ClipboardList,  comingSoon: true  },
  { key: "operational_prefs",    icon: Wrench,         comingSoon: false },
  { key: "qa_prefs",             icon: ClipboardList,  comingSoon: true  },
  { key: "admin_prefs",          icon: ClipboardList,  comingSoon: true  },
  { key: "observer_prefs",       icon: ClipboardList,  comingSoon: true  },
];

// ----------------------------------------------------------------------------
// Generic section wrappers
// ----------------------------------------------------------------------------
const ComingSoonCard = ({ t }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6 text-center" data-testid="settings-coming-soon">
    <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
    <h3 className="font-semibold text-slate-800">{t("settings.comingSoon")}</h3>
    <p className="text-sm text-slate-500 mt-1">{t("settings.comingSoonNote")}</p>
  </div>
);

// FINAL-DEMO-PROFILES-QUESTIONNAIRES-RL-OPS-FIX-1 (Feb 2026):
// Operational preferences — Preview-controlled section. Read-only
// for the moment (no backend write). All inputs disabled with a
// "Preview controlado" badge so the demo can showcase the surface
// without any DB write. Activation gated by
// `PLAN OPERATIONAL-PREFERENCES-REAL-SETTINGS-1`.
const OPS_PREFS_COPY = {
  es: {
    title: "Preferencias operativas",
    sub: "Configura cómo gestionas tu trabajo en Racket Lab: tienda asociada, disponibilidad, servicios, prioridades y materiales. Preview controlado · sin escritura en base de datos.",
    badge: "Preview controlado",
    blockedNote: "Edición bloqueada · activación bajo GO nominal · PLAN OPERATIONAL-PREFERENCES-REAL-SETTINGS-1",
    fields: {
      shop:           { label: "Tienda / Taller asociado",   value: "SS8 Demo Shop · ES" },
      availability:   { label: "Disponibilidad semanal",      value: "Lun–Vie 09:00–18:00 · Sáb 09:00–14:00" },
      services:       { label: "Servicios que realizas",      value: "Encordado · Cambio de overgrip · Plomado · Personalización · Reparaciones" },
      shifts:         { label: "Franjas / turnos preferidos", value: "Mañana (alta prioridad) · Tarde (media)" },
      workType:       { label: "Tipo de trabajo preferido",   value: "Encordados de competición · Personalización avanzada" },
      notifications:  { label: "Notificaciones operativas",   value: "Nuevos tickets · Vencimientos · Cambios de prioridad" },
      priority:       { label: "Prioridad de tickets",        value: "Competición > Académico > General" },
      materials:      { label: "Materiales / equipamiento",   value: "Luxilon · Babolat RPM · Yonex Poly Tour · Wilson NXT" },
      workLanguage:   { label: "Idioma de trabajo",           value: "Español · Inglés" },
      capabilities:   { label: "Capacidades técnicas",        value: "Encordado 4 nudos · 2 nudos · híbrido · plomado balance · personalización mango" },
      queue:          { label: "Cola / colas asignadas",      value: "Cola principal SS8 Demo Shop · cola overflow opcional" },
      notes:          { label: "Notas internas",              value: "(Preview) — espacio reservado para observaciones operativas del responsable de tienda." },
    },
  },
  en: {
    title: "Operational preferences",
    sub: "Configure how you handle your Racket Lab work: associated shop, availability, services, priorities and materials. Controlled preview · no database writes.",
    badge: "Controlled preview",
    blockedNote: "Edit blocked · activation under nominal GO · PLAN OPERATIONAL-PREFERENCES-REAL-SETTINGS-1",
    fields: {
      shop:           { label: "Associated shop / workshop", value: "SS8 Demo Shop · ES" },
      availability:   { label: "Weekly availability",         value: "Mon–Fri 09:00–18:00 · Sat 09:00–14:00" },
      services:       { label: "Services you perform",        value: "Stringing · Overgrip change · Lead balance · Customization · Repairs" },
      shifts:         { label: "Preferred shifts / slots",    value: "Morning (high priority) · Afternoon (medium)" },
      workType:       { label: "Preferred work type",         value: "Competition stringing · Advanced customization" },
      notifications:  { label: "Operational notifications",   value: "New tickets · Due dates · Priority changes" },
      priority:       { label: "Ticket priority",             value: "Competition > Academic > General" },
      materials:      { label: "Materials / equipment",       value: "Luxilon · Babolat RPM · Yonex Poly Tour · Wilson NXT" },
      workLanguage:   { label: "Work language",               value: "Spanish · English" },
      capabilities:   { label: "Technical capabilities",      value: "4-knot stringing · 2-knot · hybrid · lead balance · handle customization" },
      queue:          { label: "Queue / assigned queues",     value: "SS8 Demo Shop main queue · optional overflow" },
      notes:          { label: "Internal notes",              value: "(Preview) — reserved space for shop manager operational notes." },
    },
  },
};

function OperationalPrefsPreviewSection({ lang }) {
  const copy = OPS_PREFS_COPY[lang] || OPS_PREFS_COPY.en;
  const fields = copy.fields;
  return (
    <div className="space-y-4" data-testid="settings-operational-prefs-preview">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{copy.title}</h2>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">{copy.sub}</p>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider shrink-0"
          data-testid="settings-operational-prefs-badge"
        >
          <ShieldCheck className="w-3 h-3" />{copy.badge}
        </span>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900"
           data-testid="settings-operational-prefs-blocked-note">
        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{copy.blockedNote}</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(fields).map(([k, f]) => (
          <div
            key={k}
            className="rounded-xl border border-slate-200 bg-white p-3"
            data-testid={`settings-operational-prefs-field-${k}`}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
              {f.label}
            </div>
            <div className="text-sm text-slate-800">{f.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SectionShell = ({ title, sub, children, onSave, saving, t }) => (
  <div className="space-y-4">
    <div>
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
    </div>
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
      {children}
    </div>
    {onSave && (
      <div className="flex justify-end">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="settings-section-save"
        >
          {saving ? "…" : (t ? t("settings.save") : "Save")}
        </Button>
      </div>
    )}
  </div>
);

// ----------------------------------------------------------------------------
// Section: Account
// ----------------------------------------------------------------------------
const AccountSection = ({ t, data, onSave, saving }) => {
  const [form, setForm] = useState(data || {});
  useEffect(() => { setForm(data || {}); }, [data]);
  return (
    <SectionShell
      t={t}
      title={t("settings.sections.account")}
      sub={t("settings.account.email_password_note")}
      onSave={() => onSave(form)}
      saving={saving}
    >
      <label className="block text-sm">
        <span className="block text-slate-700 mb-1">{t("settings.account.privacy_visibility")}</span>
        <select
          className="w-full border rounded px-2 py-2 bg-white"
          value={form.privacy_visibility || "network"}
          onChange={(e) => setForm({ ...form, privacy_visibility: e.target.value })}
          data-testid="settings-account-visibility"
        >
          <option value="public">{t("settings.account.visibility_public")}</option>
          <option value="network">{t("settings.account.visibility_network")}</option>
          <option value="private">{t("settings.account.visibility_private")}</option>
        </select>
      </label>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.communication_email}
            onChange={(e) => setForm({ ...form, communication_email: e.target.checked })}
            data-testid="settings-account-comm-email"
          />
          {t("settings.account.comm_email")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.communication_in_app}
            onChange={(e) => setForm({ ...form, communication_in_app: e.target.checked })}
            data-testid="settings-account-comm-in-app"
          />
          {t("settings.account.comm_in_app")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.communication_campaign}
            onChange={(e) => setForm({ ...form, communication_campaign: e.target.checked })}
            data-testid="settings-account-comm-campaign"
          />
          {t("settings.account.comm_campaign")}
        </label>
      </div>
    </SectionShell>
  );
};

// ----------------------------------------------------------------------------
// Generic key/value sections — minimal P5.2 surface; deeper UI lands in P5.3+.
// ----------------------------------------------------------------------------
const SimpleSection = ({ title, sub, data, onSave, saving, fields, t, lang, userSport }) => {
  const [form, setForm] = useState(data || {});
  useEffect(() => { setForm(data || {}); }, [data]);
  const label = (f) => f.labelKey ? t(f.labelKey) : f.label;
  return (
    <SectionShell t={t} title={title} sub={sub} onSave={() => onSave(form)} saving={saving}>
      {fields.map((field) => {
        const { key, kind = "text" } = field;
        const v = form[key];
        if (kind === "bool") {
          return (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!v}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                data-testid={`settings-field-${key}`}
              />
              {label(field)}
            </label>
          );
        }
        if (kind === "textarea") {
          return (
            <label key={key} className="block text-sm">
              <span className="block text-slate-700 mb-1">{label(field)}</span>
              <Textarea
                rows={2}
                value={v || ""}
                maxLength={500}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`settings-field-${key}`}
              />
            </label>
          );
        }
        if (kind === "select") {
          // Phase 1 — closed enum dropdown. Backend Pydantic enforces
          // Literal[…] so free-text inputs risk 422; rendering as <select>
          // removes typo risk and clarifies the closed value set.
          return (
            <label key={key} className="block text-sm">
              <span className="block text-slate-700 mb-1">{label(field)}</span>
              <select
                value={v || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value || null })}
                className="w-full border rounded-md px-3 py-2 bg-white text-sm text-slate-800"
                data-testid={`settings-field-${key}`}
              >
                <option value="">{t("settings.selectPlaceholder") || "—"}</option>
                {(field.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.labelKey ? t(opt.labelKey) : opt.label}
                  </option>
                ))}
              </select>
            </label>
          );
        }
        if (kind === "csv") {
          return (
            <label key={key} className="block text-sm">
              <span className="block text-slate-700 mb-1">{label(field)}</span>
              <Input
                value={Array.isArray(v) ? v.join(", ") : (v || "")}
                onChange={(e) => setForm({
                  ...form,
                  [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                })}
                data-testid={`settings-field-${key}`}
              />
              <span className="block text-[11px] text-slate-400 mt-0.5">{t("settings.csvHint")}</span>
            </label>
          );
        }
        // Wishlist v2 — structured multi-selects backed by /api/catalog/*.
        if (kind === "brand_multiselect") {
          const otherKey = field.otherKey || "custom_brand_other";
          return (
            <div key={key}>
              <BrandMultiSelect
                t={t}
                value={Array.isArray(v) ? v : []}
                customValue={Array.isArray(form[otherKey]) ? form[otherKey] : []}
                onChange={(arr) => setForm((prev) => ({ ...prev, [key]: arr }))}
                onCustomChange={(arr) => setForm((prev) => ({ ...prev, [otherKey]: arr }))}
                userSport={userSport}
              />
            </div>
          );
        }
        if (kind === "category_multiselect") {
          const otherKey = field.otherKey || "custom_category_other";
          return (
            <div key={key}>
              <ProductCategoryMultiSelect
                t={t}
                lang={lang}
                value={Array.isArray(v) ? v : []}
                customValue={Array.isArray(form[otherKey]) ? form[otherKey] : []}
                onChange={(arr) => setForm((prev) => ({ ...prev, [key]: arr }))}
                onCustomChange={(arr) => setForm((prev) => ({ ...prev, [otherKey]: arr }))}
              />
            </div>
          );
        }
        if (kind === "sponsorship_category_multiselect") {
          const otherKey = field.otherKey || "custom_sponsorship_category_other";
          return (
            <div key={key}>
              <SponsorshipCategoryMultiSelect
                t={t}
                lang={lang}
                value={Array.isArray(v) ? v : []}
                customValue={Array.isArray(form[otherKey]) ? form[otherKey] : []}
                onChange={(arr) => setForm((prev) => ({ ...prev, [key]: arr }))}
                onCustomChange={(arr) => setForm((prev) => ({ ...prev, [otherKey]: arr }))}
              />
            </div>
          );
        }
        return (
          <label key={key} className="block text-sm">
            <span className="block text-slate-700 mb-1">{label(field)}</span>
            <Input
              value={v || ""}
              maxLength={200}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`settings-field-${key}`}
            />
          </label>
        );
      })}
    </SectionShell>
  );
};

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
const MySettings = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const location = useLocation();

  // WLP Adoption B3.1 + DASHBOARD-SIDEMENU-PROFILES-1-FIX2 / FIX4
  // (Feb 2026). Honour URL hashes emitted by the side-menu submenu
  // items ("Mi perfil" section). Each hash maps to a section key from
  // SECTION_ORDER so deep linking /app/settings#favorites lands
  // directly on the Favoritos tab.
  //
  // FIX4: react-router's `navigate()` uses pushState which does NOT
  // fire the native `hashchange` event, so the previous single
  // `useEffect([])` only worked on first mount. We now derive the
  // section directly from `location.hash` (with an override slot for
  // in-page tab switches like the WishlistAdoptionBanner CTA). Pattern
  // follows React's recommended "adjusting state when prop changes" —
  // a conditional setState during render is fine and avoids the
  // `react-hooks/set-state-in-effect` lint warning.
  const HASH_MAP = {
    "#account":              "account",
    "#calendar":             "calendar",
    "#notifications":        "notifications",
    "#social":               "social_links",
    "#family":               "relationships",
    "#favorites":            "favorites",
    "#hobbies":              "hobbies",
    "#friends":              "network",
    "#messaging":            "messaging",
    "#wishlist-prefs":       "wishlist_preferences",
    "#wishlist-preferences": "wishlist_preferences",
    "#travel-prefs":         "travel",
    "#profile-data":         "profile_data",
    // PROFILE-SETTINGS-ALL-PROFILES-ALIGN-1 (Feb 2026) — per-role
    // placeholder hashes emitted by DashboardSideMenu when the user
    // belongs to a professional / operational / QA / admin / observer
    // track. All resolve to ComingSoon sections (no real backend yet).
    "#pro-prefs":            "professional_prefs",
    "#ops-prefs":            "operational_prefs",
    "#qa-prefs":             "qa_prefs",
    "#admin-prefs":          "admin_prefs",
    "#observer-prefs":       "observer_prefs",
  };
  const hashSection = HASH_MAP[location.hash] || null;
  const [activeOverride, setActiveOverride] = useState(null);
  const [lastHash, setLastHash] = useState(location.hash);
  if (location.hash !== lastHash) {
    setLastHash(location.hash);
    setActiveOverride(null);
  }
  const active = activeOverride || hashSection || "account";
  const setActive = (key) => setActiveOverride(key);

  // P5.3 — owned profile_keys driving the active-profile selector.
  // user.profile_key is always present; test_profile_keys is the QA
  // multi-rol extension (admin-assigned today, public flow later).
  const ownedProfiles = useMemo(() => {
    const set = new Set();
    if (user?.profile_key) set.add(user.profile_key);
    (user?.test_profile_keys || []).forEach((k) => set.add(k));
    return Array.from(set);
  }, [user]);

  const [activeProfileKey, setActiveProfileKey] = useState(user?.profile_key || ownedProfiles[0] || null);

  // Keep the active profile in sync if the user object is rehydrated
  // after a session refresh (e.g. via /auth/me).
  useEffect(() => {
    if (user?.profile_key && !activeProfileKey) {
      setActiveProfileKey(user.profile_key);
    }
  }, [user, activeProfileKey]);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/users/me/settings"),
        api.get("/users/me/profile-data"),
      ]);
      setSettings(s.data);
      setProfileData(p.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load_failed"));
    }
  };

  useEffect(() => { load(); }, []);

  const saveSection = async (sectionKey, value) => {
    setSaving(true);
    try {
      const res = await api.put(`/users/me/settings/${sectionKey}`, value);
      // D3-X (Feb 2026) — backend returns `mode: "demo"` when the
      // write landed in the sandbox overlay instead of the real
      // `user_settings` collection. Surface that distinctly so the
      // admin understands the change is session-scoped and does not
      // affect production data. Mirrors the profile-data save path.
      if (res?.data?.mode === "demo") {
        toast.success(t("settings.savedInDemo"));
      } else {
        toast.success(t("settings.saved"));
      }
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("settings.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const saveProfileCommon = async (value) => {
    setSaving(true);
    try {
      const res = await api.put("/users/me/profile-data/common", value);
      // D3-A — server returns `mode: "demo"` when the write landed
      // in the sandbox overlay rather than the real collection. We
      // surface that to the user with a distinctly-coloured toast.
      if (res?.data?.mode === "demo") {
        toast.success(t("settings.savedInDemo"));
      } else {
        toast.success(t("settings.saved"));
      }
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("settings.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  // Phase-1 Profile Data scope §3 — Level 2 segmentation persists
  // through its own endpoint. Storage stays under the unified `common`
  // doc on the backend (no DB migration), but the UI treats it as a
  // distinct logical block.
  const saveProfileSegmentation = async (value) => {
    setSaving(true);
    try {
      const res = await api.put("/users/me/profile-data/segmentation", value);
      if (res?.data?.mode === "demo") {
        toast.success(t("settings.savedInDemo"));
      } else {
        toast.success(t("settings.saved"));
      }
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("settings.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  // P5.3 — Persist the Level 3 block for the active profile_key.
  // Backend enforces 403 if profile_key is not owned by the user, so
  // we don't duplicate the ownership check here.
  const saveProfileBlock = async (profileKey, value) => {
    setSaving(true);
    try {
      await api.put(`/users/me/profile-data/profile/${profileKey}`, value);
      toast.success(t("settings.saved"));
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("settings.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const currentSection = useMemo(() => SECTION_ORDER.find((s) => s.key === active), [active]);

  const renderSection = () => {
    if (!settings) return null;
    if (active === "operational_prefs") {
      return <OperationalPrefsPreviewSection lang={lang} />;
    }
    if (currentSection?.comingSoon) return <ComingSoonCard t={t} />;
    if (active === "account") {
      return <AccountSection t={t} data={settings.account} saving={saving} onSave={(v) => saveSection("account", v)} />;
    }
    if (active === "profile_data") {
      // Special composite section: Common (L1) → Segmentation (L2) →
      // active-profile selector → Level 3 block. The three cards stay
      // visually distinct per Profile Data scope §1 so the UX matches
      // the normative "4-blocks separated" rule (internal_control
      // lives in a future Phase 2 admin surface).
      const blockFields = activeProfileKey ? PROFILE_BLOCK_FIELDS[activeProfileKey] : null;
      const blockData = activeProfileKey ? (profileData?.by_type?.[activeProfileKey] || {}) : {};
      const blockTitle = activeProfileKey ? t(`profileBlock.blockTitle.${activeProfileKey}`) : null;
      return (
        <div className="space-y-5">
          <SimpleSection
            t={t}
            title={t("settings.sections.profile_data")}
            sub={t("settings.pageSub")}
            data={profileData?.common}
            saving={saving}
            onSave={(v) => saveProfileCommon(v)}
            fields={SECTION_FIELDS.profile_common}
          />
          <SimpleSection
            t={t}
            title={t("settings.sections.profile_segmentation")}
            sub={t("settings.profileSegmentationSub")}
            data={profileData?.segmentation || profileData?.common}
            saving={saving}
            onSave={(v) => saveProfileSegmentation(v)}
            fields={SECTION_FIELDS.profile_segmentation}
          />
          {ownedProfiles.length > 0 && (
            <>
              <ActiveProfileSelector
                ownedProfiles={ownedProfiles}
                activeKey={activeProfileKey}
                onChange={setActiveProfileKey}
              />
              {activeProfileKey && MEDICAL_SENSITIVE_PROFILES.has(activeProfileKey) && (
                <MedicalConsentBanner
                  t={t}
                  consented={Boolean(blockData?.consent_aggregated_research_use)}
                />
              )}
              {blockFields ? (
                <SimpleSection
                  t={t}
                  title={`${t("profileBlock.header")} — ${blockTitle}`}
                  sub={t("profileBlock.sub")}
                  data={blockData}
                  saving={saving}
                  onSave={(v) => saveProfileBlock(activeProfileKey, v)}
                  fields={blockFields}
                />
              ) : (
                <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500" data-testid="profile-block-empty">
                  {t("profileBlock.noBlockYet")}
                </div>
              )}
            </>
          )}
        </div>
      );
    }
    // Generic config-driven sections.
    const configKey = active === "wishlist_preferences" ? "wishlist_preferences" : active;
    const fields = SECTION_FIELDS[configKey];
    if (!fields) return null;
    const dataMap = {
      calendar: settings.calendar,
      notifications: settings.notifications,
      social_links: settings.social_links,
      favorites: settings.favorites,
      hobbies: settings.hobbies,
      wishlist_preferences: settings.wishlist_preferences,
      travel: settings.travel,
    };
    // Read user's preferred sport from L2 segmentation for the optional
    // brand sport-filter toggle. Default empty → no filter offered.
    const userSport = (profileData?.common?.primary_sport || "").toLowerCase();
    return <SimpleSection
      t={t}
      lang={lang}
      userSport={userSport}
      title={t(`settings.sections.${active}`)}
      data={dataMap[active]}
      saving={saving}
      onSave={(v) => saveSection(active, v)}
      fields={fields}
    />;
  };

  return (
    <PanelLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="my-settings-page">
          {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX3 (Feb 2026) — `Volver al
              panel` button retired. The persistent left side menu's
              `Panel` entry is now the universal return point.
              FIX5 (Feb 2026) — page H1 is now dynamic: it always
              mirrors the section title corresponding to the active
              submenu so users never see "Mi perfil y ajustes" once
              they have drilled into a specific section (Mensajería,
              Familia y relaciones, etc.). Subtitle stays generic.
              For ComingSoon sections (relationships / network /
              messaging) the H1 is still the section name; the
              `ComingSoonCard` rendered by `renderSection` adds the
              status note below. */}
          <div>
            <h1
              className="text-2xl font-bold text-slate-900"
              data-testid="my-settings-section-title"
            >
              {t(`settings.sections.${active}`)}
            </h1>
            <p className="text-sm text-slate-600 mt-1">{t("settings.pageSub")}</p>
          </div>

          {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX2 (Feb 2026) — both
              top-line blocks (Tu perfil TEAM + Tus preferencias de
              wishlist) are kept ALWAYS visible inside `Mi perfil` and
              laid out in a single row of two roughly half-width
              columns. Each component renders to `null` when its own
              gating conditions short-circuit, so the row collapses
              gracefully (the surviving block still appears full row). */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="my-settings-toprow"
          >
            <div data-testid="my-settings-toprow-profile">
              <ProfileCompletionBar variant="settings" />
            </div>
            <div data-testid="my-settings-toprow-wishlist">
              <WishlistAdoptionBanner onActivate={() => setActive("wishlist_preferences")} />
            </div>
          </div>

          {/* DASHBOARD-SIDEMENU-PROFILES-1-FIX3 (Feb 2026) — central
              `Mi perfil` submenu (both mobile picker AND desktop
              left-aside list) retired. Navigation is now exclusively
              driven by the persistent global side menu. The page only
              renders the active section's content. */}
          <main data-testid="my-settings-content">
            {/* WLP Adoption B3.1 — always-present scroll target so the
                modal CTA + banner deep-link (#wishlist-preferences)
                works regardless of which tab is currently active.
                The useEffect hash listener separately activates the
                wishlist_preferences tab when the hash matches. */}
            <span id="wishlist-preferences" aria-hidden="true" className="block" />
            {renderSection()}
          </main>
        </div>
      </div>
    </PanelLayout>
  );
};

export default MySettings;
